import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FieldData, PARTNER_USER_ROLE_OPTIONS, Select, MaterialSymbol, withStyles, Card } from 'vopay';
import { Button, Title } from 'UI';
import type { OpenDialogProps } from '@/providers/Dialog.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useChangeUserRole from '@/hooks/useApi/useChangeUserRole';
import styles from './ManageRoleDialog.module.scss';

const cn = withStyles(styles);

// TODO: currentRole/Role stay a plain string (not a narrowed 'Admin' |
// 'Support' | 'Viewer' union) for the same reason as UserType in
// useComputePartnerRoleCounts.ts — currentRole is fed by UsersList's
// data.UserType, which comes from AccountUser, shared with client-dashboard's
// dynamic/custom account roles. Narrowing here would be inaccurate upstream.
// Revisit once partner portal gets its own roles endpoint/response type.
type ManageRoleDialogProps = OpenDialogProps<boolean> & {
  emailAddress: string;
  currentRole: string;
};

type ManageRoleFormValues = {
  Role: string;
};

export default function ManageRoleDialog({ onConfirm, onCancel, emailAddress, currentRole }: ManageRoleDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.List.ManageRoleDialog' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const { changeUserRole } = useChangeUserRole();

  const roleLabel = t('roleLabel', { defaultValue: 'Role' });
  const roleOptions = useMemo(
    () => Object.entries(PARTNER_USER_ROLE_OPTIONS).map(([value, label]) => ({ value, label })),
    [],
  );

  const config = useMemo<FieldData<ManageRoleFormValues>[]>(
    () => [
      {
        fieldName: 'Role',
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => (
          <Select
            version="2"
            {...getFormikData<ManageRoleFormValues['Role']>({})}
            options={roleOptions}
            placeholder={roleLabel}
            sort={false}
          />
        ),
      },
    ],
    [roleLabel, roleOptions],
  );

  const { ctx, renderFormElements } = useForm<ManageRoleFormValues>({
    initialValues: { Role: currentRole },
    config,
    onSubmit: async (values) => {
      try {
        await changeUserRole({ PartnerUserName: emailAddress, Role: values.Role });
        onConfirm(true);
      } catch {
        onConfirm(false);
      }
    },
  });

  return (
    <Card hoverEffect={false} className={cn('body')}>
      <MaterialSymbol icon="groups" fillAmount={1} size="40px" className={cn('icon')} />
      <Title level="3" className={cn('title')}>
        {t('title', { defaultValue: 'Manage Role' })}
      </Title>

      <p className={cn('subtitle')}>
        {t('description', { defaultValue: 'Change role to ' })}
        <span className={cn('subtitleEmail')}>{emailAddress}</span>
      </p>

      <form className={cn('form')} onSubmit={ctx.handleSubmit}>
        {renderFormElements({ config })}

        <div className={cn('footer')}>
          <Button id="manage-role-cancel" colorScheme="secondary" type="button" onClick={onCancel}>
            {tGlobal('cancel', { defaultValue: 'Cancel' })}
          </Button>

          <Button
            id="manage-role-confirm"
            type="submit"
            colorScheme="primary"
            disabled={isRoleViewOnly || !ctx.isValid || ctx.isSubmitting}
          >
            {tGlobal('confirm', { defaultValue: 'Confirm' })}
          </Button>
        </div>
      </form>
    </Card>
  );
}
